import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getAdvancedFlags } from "../../../../services/rules";
import { errorMsg, SORT_DIRECTION } from "../../../../utils/vars";

const DEFAULT_TAKE = 20;

const DEFAULT_FILTERS = {
  id: "",
  ruleName: "",
  startDate: "",
  endDate: "",
  exactDate: "",
};

const useFlagsAdvancedSearch = () => {
  const { id } = useParams();
  const [page, setPage] = useState(0);
  const [take, setTake] = useState(DEFAULT_TAKE);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sort, setSort] = useState({
    direction: SORT_DIRECTION.DESC,
    type: "DATE",
  });

  const filterQueryString = useMemo(() => {
    const searchParams = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        searchParams.append(key, value);
      }
    });

    return searchParams.toString();
  }, [filters]);

  const queryParams = useMemo(
    () => ({
      id,
      page,
      size: take,
      direction: sort.direction,
      type: sort.type,
      filterParams: filterQueryString ? `&${filterQueryString}` : "",
    }),
    [page, id, sort.direction, sort.type, take, filterQueryString],
  );

  const { data, error, isFetching } = useQuery({
    queryKey: ["advanced-flags", queryParams],
    queryFn: () => getAdvancedFlags(queryParams),
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (error) {
      toast.error(error.response?.data?.message || errorMsg, {
        toastId: "advanced-flags-error",
      });
    }
  }, [error]);

  const toggleSort = (type) => {
    setSort((currentSort) => {
      if (currentSort.type === type) {
        return {
          type,
          direction:
            currentSort.direction === SORT_DIRECTION.DESC
              ? SORT_DIRECTION.ASC
              : SORT_DIRECTION.DESC,
        };
      }

      return {
        type,
        direction: SORT_DIRECTION.DESC,
      };
    });

    setPage(0);
  };

  const handleFilterChange = (key, value) => {
    setFilters((currentFilters) => {
      if (key === "DATE") {
        return {
          ...currentFilters,
          startDate: value.startDate || "",
          endDate: value.endDate || "",
          exactDate: value.exactDate || "",
        };
      }

      return {
        ...currentFilters,
        [key]: value,
      };
    });

    setPage(0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage - 1);
  };

  const handleTakeChange = (newTake) => {
    setTake(newTake);
  };

  return {
    filters,
    handleFilterChange,
    handlePageChange,
    handleTakeChange,
    id,
    isFetching,
    page,
    rowsData: data?.data || [],
    sort,
    take,
    toggleSort,
    totalCount: data?.totalCount || 0,
  };
};

export default useFlagsAdvancedSearch;
